import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  Building2,
  FileText,
  ArrowRight,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Compliance = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCompliance = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/compliance/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch compliance data:', error);
      toast.error('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCompliance();
  }, [fetchCompliance]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <Clock className="w-5 h-5 text-orange-500" />;
      case 'non_compliant':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      compliant: 'status-compliant',
      warning: 'status-warning',
      non_compliant: 'status-non_compliant'
    };
    return styles[status] || 'status-inactive';
  };

  const getStatusLabel = (status) => {
    const labels = {
      compliant: 'Compliant',
      warning: 'Warning',
      non_compliant: 'Non-Compliant'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="compliance-page">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading tracking-tight">Compliance</h1>
        <p className="text-muted-foreground mt-1">
          Monitor supplier compliance status and document requirements
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-medium text-muted-foreground">Compliance Rate</p>
              <Badge variant="secondary">{data?.overview?.compliance_rate || 0}%</Badge>
            </div>
            <Progress value={data?.overview?.compliance_rate || 0} className="h-2" />
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-green-500/50 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Compliant</p>
                <p className="text-3xl font-bold font-heading text-green-600 dark:text-green-400">
                  {data?.overview?.compliant || 0}
                </p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-orange-500/50 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Warning</p>
                <p className="text-3xl font-bold font-heading text-orange-600 dark:text-orange-400">
                  {data?.overview?.warning || 0}
                </p>
              </div>
              <div className="p-3 bg-orange-500/10 rounded-lg">
                <Clock className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:border-red-500/50 transition-colors">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Non-Compliant</p>
                <p className="text-3xl font-bold font-heading text-red-600 dark:text-red-400">
                  {data?.overview?.non_compliant || 0}
                </p>
              </div>
              <div className="p-3 bg-red-500/10 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Suppliers List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading">Supplier Compliance Status</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.suppliers?.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No suppliers to track</h3>
              <p className="text-muted-foreground">
                Add suppliers and upload documents to track compliance
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Non-compliant first, then warning, then compliant */}
              {[...data.suppliers]
                .sort((a, b) => {
                  const order = { non_compliant: 0, warning: 1, compliant: 2 };
                  return order[a.status] - order[b.status];
                })
                .map((supplier) => (
                  <Link
                    key={supplier.supplier_id}
                    to={`/suppliers/${supplier.supplier_id}`}
                    className="block"
                    data-testid={`compliance-supplier-${supplier.supplier_id}`}
                  >
                    <div className={`p-4 rounded-lg border transition-all hover:shadow-md ${
                      supplier.status === 'non_compliant' 
                        ? 'border-red-500/30 bg-red-500/5' 
                        : supplier.status === 'warning'
                        ? 'border-orange-500/30 bg-orange-500/5'
                        : 'border-border hover:border-primary/50'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {getStatusIcon(supplier.status)}
                          <div>
                            <p className="font-medium">{supplier.company_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {supplier.total_documents} document{supplier.total_documents !== 1 ? 's' : ''} uploaded
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {supplier.manually_compliant && (
                            <Badge variant="outline" className="text-xs flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" />
                              Verified
                            </Badge>
                          )}
                          <Badge className={getStatusBadge(supplier.status)}>
                            {getStatusLabel(supplier.status)}
                          </Badge>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>

                      {/* Issues */}
                      {(supplier.missing_documents?.length > 0 || supplier.expired_documents?.length > 0 || supplier.expiring_documents?.length > 0) && (
                        <div className="mt-4 pt-4 border-t border-border/50 space-y-2">
                          {supplier.missing_documents?.length > 0 && (
                            <div className="flex items-start gap-2 text-sm">
                              <FileText className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                              <span className="text-red-600 dark:text-red-400">
                                Missing: {supplier.missing_documents.map(t => t.replace('_', ' ')).join(', ')}
                              </span>
                            </div>
                          )}
                          {supplier.expired_documents?.length > 0 && (
                            <div className="flex items-start gap-2 text-sm">
                              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                              <span className="text-red-600 dark:text-red-400">
                                {supplier.expired_documents.length} expired document{supplier.expired_documents.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                          {supplier.expiring_documents?.length > 0 && (
                            <div className="flex items-start gap-2 text-sm">
                              <Clock className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                              <span className="text-orange-600 dark:text-orange-400">
                                {supplier.expiring_documents.length} document{supplier.expiring_documents.length !== 1 ? 's' : ''} expiring soon
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Link>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Compliance;
